import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, Chip,
  Paper, LinearProgress, Tooltip,
} from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { updateTire, isNearEndOfLife } from '../../utils/tiresService';

export default function TireLifingForm({ open, onClose, onSaved, tires = [], title }) {
  const { t } = useTranslation('tires');
  const [delta, setDelta] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [bulkKm, setBulkKm]         = useState('');
  const [bulkCycles, setBulkCycles] = useState('');

  useEffect(() => {
    if (open) {
      const init = {};
      tires.forEach(tr => { init[tr.id] = { km: '', cycles: '' }; });
      setDelta(init);
      setBulkKm('');
      setBulkCycles('');
      setError('');
    }
  }, [open, tires]);

  const setTireDelta = (id, field, val) => {
    setDelta(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  };

  const applyBulk = () => {
    const km = bulkKm.trim();
    const cycles = bulkCycles.trim();
    setDelta(prev => {
      const next = { ...prev };
      tires.forEach(tr => {
        next[tr.id] = {
          km:     km     !== '' ? km     : (prev[tr.id]?.km     || ''),
          cycles: cycles !== '' ? cycles : (prev[tr.id]?.cycles || ''),
        };
      });
      return next;
    });
  };

  const handleSave = async () => {
    setError('');
    const ops = tires
      .map(tr => {
        const d = delta[tr.id] || {};
        const addKm     = d.km     !== '' && !isNaN(Number(d.km))     ? Number(d.km)     : 0;
        const addCycles = d.cycles !== '' && !isNaN(Number(d.cycles)) ? Number(d.cycles) : 0;
        if (addKm === 0 && addCycles === 0) return null;
        return {
          id: tr.id,
          patch: {
            kmTotal:    (tr.kmTotal    || 0) + addKm,
            heatCycles: (tr.heatCycles || 0) + addCycles,
          },
        };
      })
      .filter(Boolean);

    if (ops.length === 0) {
      setError(t('lifing_error_empty'));
      return;
    }

    setSaving(true);
    try {
      await Promise.all(ops.map(op => updateTire(op.id, op.patch)));
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || t('lifing_error_save'));
    } finally {
      setSaving(false);
    }
  };

  const isMultiple = tires.length > 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || (isMultiple ? t('lifing_title_bulk') : t('lifing_title_single'))}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isMultiple && (
          <Box sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
              {t('lifing_bulk_helper')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField size="small" type="number" label={t('lifing_field_km')} value={bulkKm} onChange={e => setBulkKm(e.target.value)} sx={{ width: 100 }} />
              <TextField size="small" type="number" label={t('lifing_field_cycles')} value={bulkCycles} onChange={e => setBulkCycles(e.target.value)} sx={{ width: 100 }} />
              <Tooltip title={t('lifing_apply_all_tooltip')}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={applyBulk}
                    disabled={!bulkKm && !bulkCycles}
                  >
                    {t('lifing_apply_all')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {tires.map(tr => (
            <TireRow key={tr.id} tire={tr} delta={delta[tr.id] || {}} onChange={(f, v) => setTireDelta(tr.id, f, v)} t={t} />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? t('saving') : t('lifing_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Riga singola gomma ──────────────────────────────────────────
function TireRow({ tire, delta, onChange, t }) {
  const addKm     = delta.km     !== '' && !isNaN(Number(delta.km))     ? Number(delta.km)     : 0;
  const addCycles = delta.cycles !== '' && !isNaN(Number(delta.cycles)) ? Number(delta.cycles) : 0;

  const newKm     = (tire.kmTotal    || 0) + addKm;
  const newCycles = (tire.heatCycles || 0) + addCycles;

  const previewTire = { ...tire, kmTotal: newKm, heatCycles: newCycles };
  const nearEol = isNearEndOfLife(previewTire);

  const kmRatio    = tire.lifeMaxKm     ? Math.min(100, (newKm     / tire.lifeMaxKm)     * 100) : 0;
  const cycleRatio = tire.lifeMaxCycles ? Math.min(100, (newCycles / tire.lifeMaxCycles) * 100) : 0;
  const worst = Math.max(kmRatio, cycleRatio);
  const hasLimit = tire.lifeMaxKm || tire.lifeMaxCycles;

  const wearColor = worst >= 80 ? 'error' : worst >= 50 ? 'warning' : 'success';
  const kmUnit = t('lifing_km_unit');
  const cyclesUnit = t('lifing_cycles_unit');

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {tire.position && (
          <Chip label={tire.position} color="primary" size="small" sx={{ fontWeight: 700, minWidth: 36 }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {tire.brand || '—'} {tire.compound && `· ${tire.compound}`}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {tire.barcode}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' }, gap: 1.5, alignItems: 'center' }}>
        {/* Valori attuali */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>{t('lifing_current')}</Typography>
          <Typography variant="body2">
            <strong>{tire.kmTotal || 0}</strong> {kmUnit}
            {tire.lifeMaxKm && <Typography component="span" variant="caption" color="text.secondary"> / {tire.lifeMaxKm}</Typography>}
            {' · '}
            <strong>{tire.heatCycles || 0}</strong> {cyclesUnit}
            {tire.lifeMaxCycles && <Typography component="span" variant="caption" color="text.secondary"> / {tire.lifeMaxCycles}</Typography>}
          </Typography>
        </Box>

        {/* Inputs */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ArrowRightAltIcon color="action" />
          <TextField size="small" type="number" label={t('lifing_delta_km')} value={delta.km || ''} onChange={e => onChange('km', e.target.value)} sx={{ width: 80 }} />
          <TextField size="small" type="number" label={t('lifing_delta_cycles')} value={delta.cycles || ''} onChange={e => onChange('cycles', e.target.value)} sx={{ width: 80 }} />
        </Box>

        {/* Preview nuovo */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>{t('lifing_after')}</Typography>
          <Typography variant="body2" sx={{ color: nearEol ? 'warning.main' : 'inherit', fontWeight: nearEol ? 700 : 400 }}>
            <strong>{newKm}</strong> {kmUnit} · <strong>{newCycles}</strong> {cyclesUnit}
          </Typography>
        </Box>
      </Box>

      {hasLimit && (addKm !== 0 || addCycles !== 0) && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={worst} color={wearColor} sx={{ height: 4, borderRadius: 2 }} />
          <Typography variant="caption" sx={{ display: 'block', fontSize: 9, textAlign: 'right', color: `${wearColor}.main`, fontWeight: 700, mt: 0.25 }}>
            {t('lifing_wear_label', { pct: Math.round(worst) })}
            {nearEol && ' · ' + t('lifing_wear_eol')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
